// The password policy is Firebase project configuration, not a product
// decision this app gets to restate. Everything here turns one policy into the
// two things the auth screens need — a live checklist and antd validators —
// derived from the same rule objects, so a form can never block a password its
// own checklist calls valid.
//
// `usePasswordPolicy` fetches the real policy at runtime. These constants are
// only the fallback for a failed fetch, and are kept equal to the production
// project's configuration so the fallback still guides a user toward a password
// that will be accepted.

// Shaped exactly like Firebase's `PasswordPolicy.customStrengthOptions`, so the
// fetched policy and this fallback flow through the same derivation. A rule the
// project does not require is absent rather than false, matching how the SDK
// reports one.
export const FALLBACK_STRENGTH_OPTIONS = {
  minPasswordLength: 8,
  maxPasswordLength: 32,
  containsLowercaseLetter: true,
  containsUppercaseLetter: true,
  containsNumericCharacter: true,
};

// One entry per rule Firebase can enforce, in the order a user reads them.
// `label` is the checklist wording, `message` the validation-error wording, and
// `test` is the single predicate both the checklist and the validator run.
const RULE_BUILDERS = [
  {
    key: "minLength",
    applies: (o) => typeof o.minPasswordLength === "number",
    build: (o) => ({
      label: `At least ${o.minPasswordLength} characters`,
      message: `Password must be at least ${o.minPasswordLength} characters.`,
      test: (p) => p.length >= o.minPasswordLength,
    }),
  },
  {
    key: "maxLength",
    applies: (o) => typeof o.maxPasswordLength === "number",
    build: (o) => ({
      label: `No more than ${o.maxPasswordLength} characters`,
      message: `Password must be at most ${o.maxPasswordLength} characters.`,
      test: (p) => p.length <= o.maxPasswordLength,
    }),
  },
  {
    key: "lowercase",
    applies: (o) => o.containsLowercaseLetter === true,
    build: () => ({
      label: "At least one lowercase letter",
      message: "Password must contain a lowercase letter.",
      test: (p) => /[a-z]/.test(p),
    }),
  },
  {
    key: "uppercase",
    applies: (o) => o.containsUppercaseLetter === true,
    build: () => ({
      label: "At least one uppercase letter",
      message: "Password must contain an uppercase letter.",
      test: (p) => /[A-Z]/.test(p),
    }),
  },
  {
    key: "numeric",
    applies: (o) => o.containsNumericCharacter === true,
    build: () => ({
      label: "At least one number",
      message: "Password must contain a number.",
      test: (p) => /[0-9]/.test(p),
    }),
  },
  {
    key: "special",
    applies: (o) => o.containsNonAlphanumericCharacter === true,
    build: () => ({
      label: "At least one special character",
      message: "Password must contain a special character.",
      test: (p) => /[^a-zA-Z0-9]/.test(p),
    }),
  },
];

/** Turn a Firebase `customStrengthOptions` object into displayable rules. */
export const rulesFromStrengthOptions = (options) =>
  RULE_BUILDERS.filter(({ applies }) => applies(options ?? {})).map(
    ({ key, build }) => ({ key, ...build(options) }),
  );

/**
 * Turn those rules into antd `Form.Item` rules.
 *
 * An empty value resolves so the field's own `required` rule owns that message
 * rather than every rule firing at once on an untouched form.
 */
export const formRulesFromRules = (rules) =>
  rules.map(({ message, test }) => ({
    validator: (_, value) =>
      !value || test(value)
        ? Promise.resolve()
        : Promise.reject(new Error(message)),
  }));

export const FALLBACK_RULES = rulesFromStrengthOptions(
  FALLBACK_STRENGTH_OPTIONS,
);
